import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useToast } from "@/components/feature/ToastProvider";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TreatDetail } from "@/lib/types";
import { cancelTreat, closeTreat, deleteTreat, joinTreat } from "../api";
import {
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  formatCountdown,
  formatDate,
  isDim,
  stateLabel,
} from "../utils";

// /treat/:no — 상세 + 신청
export default function TreatDetailPage() {
  const { no } = useParams();
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();
  const now = useNow();

  const { data, loading, error, reload, serverTs } = useApi<TreatDetail>(
    no ? `treats.php?no=${no}` : null
  );

  const [item, setItem] = useState<TreatDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (data) {
      setItem(data);
      setConfirmDel(false);
    }
  }, [data]);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;

  const becomeJoin = useCallback(async () => {
    if (!no) return;
    if (!member) {
      requireLogin(`/treat/${no}`);
      return;
    }
    setBusy(true);
    try {
      const res = await joinTreat(Number(no));
      toast(res.msg || "신청되었습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "신청에 실패했습니다.", "error");
      reload();
    } finally {
      setBusy(false);
    }
  }, [no, member, requireLogin, toast, reload]);

  const doCancel = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await cancelTreat(Number(no));
      toast(res.msg || "신청을 취소했습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "취소에 실패했습니다.", "error");
      reload();
    } finally {
      setBusy(false);
    }
  }, [no, toast, reload]);

  const doClose = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await closeTreat(Number(no));
      toast(res.msg || "모집을 마감했습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "마감에 실패했습니다.", "error");
    } finally {
      setBusy(false);
    }
  }, [no, toast, reload]);

  const doDelete = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await deleteTreat(Number(no));
      toast(res.msg || "삭제했습니다.", "success");
      if (typeof window.REACT_APP_NAVIGATE === "function") {
        window.REACT_APP_NAVIGATE("/treat");
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setConfirmDel(false);
    } finally {
      setBusy(false);
    }
  }, [no, toast]);

  if (loading) {
    return (
      <>
        <PageBanner
          title="오늘은 내가 쏜다"
          breadcrumb={[{ label: "내가쏜다", to: "/treat" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl space-y-4 px-4 py-10 md:px-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </section>
      </>
    );
  }

  if (error || !item) {
    return (
      <>
        <PageBanner
          title="오늘은 내가 쏜다"
          breadcrumb={[{ label: "내가쏜다", to: "/treat" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl px-4 py-12 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="번개를 불러오지 못했습니다"
            description={error ?? "존재하지 않는 모임입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  const remaining = Math.max(0, (item.end_in ?? 0) - elapsed);
  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const left = item.left_cnt;
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;
  const open = item.state === "open";
  const dim = isDim(item.state);
  const joins = item.joins ?? [];

  const btnBase =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50";

  return (
    <>
      <PageBanner
        title="오늘은 내가 쏜다"
        breadcrumb={[{ label: "내가쏜다", to: "/treat" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
        <article
          className={`relative overflow-hidden rounded-xl border bg-background-50 ${
            STATE_BORDER[item.state]
          } ${dim ? "opacity-80" : ""}`}
        >
          <span
            className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
            aria-hidden="true"
          ></span>

          <div className="p-6 pl-7 md:p-8 md:pl-9">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[item.state]}`}
              >
                {stateLabel(item.state, item.state_txt)}
              </span>
              {item.is_today === 1 && (
                <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  오늘
                </span>
              )}
              {mine && (
                <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
                  내가 쏘는 모임
                </span>
              )}
              {joined && !mine && (
                <span className="inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                  신청완료
                </span>
              )}
            </div>

            <h2 className="mt-4 flex items-start gap-2 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              <span aria-hidden="true">🍚</span>
              {item.subject}
            </h2>

            {/* 주최자 */}
            <div className="mt-4 flex items-center gap-3">
              <Avatar name={item.host?.name} photo={item.host?.photo} size={44} />
              <span className="text-sm text-foreground-600">
                <strong className="font-semibold text-foreground-900">
                  {item.host?.aho} {item.host?.name}
                </strong>{" "}
                님이 쏩니다
              </span>
            </div>

            {/* 정보 */}
            <dl className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-background-200 bg-background-200 sm:grid-cols-2">
              <div className="bg-background-50 px-4 py-3">
                <dt className="text-xs text-foreground-500">날짜</dt>
                <dd className="mt-0.5 text-sm font-semibold text-foreground-900">
                  {formatDate(item.tdate) || "오늘"}
                </dd>
              </div>
              <div className="bg-background-50 px-4 py-3">
                <dt className="text-xs text-foreground-500">만나는 시각</dt>
                <dd className="mt-0.5 text-sm font-semibold text-foreground-900">
                  {item.meet_time || "미정"}
                </dd>
              </div>
              <div className="bg-background-50 px-4 py-3">
                <dt className="text-xs text-foreground-500">장소</dt>
                <dd className="mt-0.5 text-sm font-semibold text-foreground-900">
                  {item.place || "-"}
                  {item.addr ? (
                    <span className="mt-0.5 block text-xs font-normal text-foreground-500">
                      {item.addr}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div className="bg-background-50 px-4 py-3">
                <dt className="text-xs text-foreground-500">인원</dt>
                <dd className="mt-0.5 text-sm font-semibold text-foreground-900">
                  {unlimited ? (
                    <>
                      {item.join_cnt}명 · 인원 제한 없음
                    </>
                  ) : (
                    <>
                      {item.join_cnt} / {item.limit_cnt}명
                      {left >= 1 && left <= 2 && (
                        <span className="ml-2 font-bold text-accent-700">
                          {left}자리 남음
                        </span>
                      )}
                    </>
                  )}
                </dd>
              </div>
            </dl>

            {/* 카운트다운 */}
            {open && (
              <div className="mt-4 flex items-center justify-between rounded-xl bg-background-100 px-5 py-3.5">
                <span className="inline-flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-alarm-warning-line" aria-hidden="true"></i>
                  폭파까지
                </span>
                <strong
                  className={`font-heading text-lg tabular-nums ${
                    cd.urgent ? "animate-pulse text-accent-700" : "text-foreground-950"
                  }`}
                >
                  {cd.text}
                </strong>
              </div>
            )}

            {item.memo && (
              <p className="mt-4 whitespace-pre-wrap rounded-xl bg-background-100 px-5 py-3.5 text-sm text-foreground-700">
                {item.memo}
              </p>
            )}

            {/* 액션 */}
            <div className="mt-6 flex flex-wrap gap-2">
              {mine ? (
                <>
                  <button
                    type="button"
                    onClick={doClose}
                    disabled={busy || !open}
                    className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                  >
                    <i className="ri-stop-circle-line" aria-hidden="true"></i>
                    조기 마감
                  </button>
                  {confirmDel ? (
                    <>
                      <button
                        type="button"
                        onClick={doDelete}
                        disabled={busy}
                        className={`${btnBase} bg-accent-500 text-primary-950 hover:bg-accent-600`}
                      >
                        삭제 확인
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDel(false)}
                        disabled={busy}
                        className={`${btnBase} border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100`}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDel(true)}
                      disabled={busy}
                      className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                    >
                      <i className="ri-delete-bin-line" aria-hidden="true"></i>
                      삭제
                    </button>
                  )}
                  <span className="flex items-center px-1 text-xs text-foreground-400">
                    주최자는 신청하지 않습니다.
                  </span>
                </>
              ) : open ? (
                joined ? (
                  <button
                    type="button"
                    onClick={doCancel}
                    disabled={busy}
                    className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                  >
                    <i className="ri-close-circle-line" aria-hidden="true"></i>
                    신청 취소 (자리 반납)
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={becomeJoin}
                    disabled={busy}
                    className={`${btnBase} bg-primary-500 text-background-50 hover:bg-primary-600`}
                  >
                    <i className="ri-hand-heart-line" aria-hidden="true"></i>
                    나도 갈래요
                  </button>
                )
              ) : (
                <span
                  className={`${btnBase} cursor-default border border-background-200 bg-background-100 text-foreground-400`}
                  aria-disabled="true"
                >
                  {stateLabel(item.state, item.state_txt)}
                </span>
              )}
            </div>
            {joined && !mine && item.my_pick ? (
              <p className="mt-3 text-sm text-foreground-500">
                회원님은{" "}
                <strong className="font-semibold text-foreground-800">
                  {item.my_pick}번째
                </strong>
                로 신청하셨습니다.
              </p>
            ) : null}
          </div>
        </article>

        {/* 신청자 명단 (상시 공개) */}
        <div className="mt-8">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-team-line text-foreground-500" aria-hidden="true"></i>
            신청자 명단
            <span className="rounded-full bg-background-100 px-2.5 py-0.5 text-sm font-semibold text-foreground-600">
              {item.join_cnt}명
            </span>
          </h3>

          {joins.length === 0 ? (
            <p className="mt-3 rounded-xl border border-dashed border-background-300 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
              아직 신청자가 없습니다. 첫 번째로 신청해 보세요!
            </p>
          ) : (
            <ol className="mt-3 space-y-2">
              {joins.map((j) => (
                <li
                  key={j.pick}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                    j.is_mine === 1
                      ? "border-primary-300 bg-primary-50"
                      : "border-background-200 bg-background-50"
                  }`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 font-heading text-xs font-bold text-foreground-500">
                    {j.pick}
                  </span>
                  <Avatar name={j.name} photo={j.photo} size={36} />
                  <span className="text-sm text-foreground-800">
                    <strong className="font-semibold">
                      {j.aho} {j.name}
                    </strong>
                    {j.is_mine === 1 && (
                      <span className="ml-2 text-xs font-semibold text-primary-700">
                        나
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* 목록으로 */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/treat"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </Link>
        </div>
      </section>
    </>
  );
}